import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  Building,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Scale,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CoBrokerScorePill } from "@/components/CoBrokerScorePill";
import { SkeletonListRow } from "@/components/Skeleton";
import { formatDateShort, formatPhp } from "@/lib/format";
import { toast } from "sonner";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

function DisputeSide({ label, listing, holderChip }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          {label}
        </div>
        {holderChip}
      </div>
      <div className="flex items-start gap-2">
        <Building className="mt-0.5 h-4 w-4 flex-none text-gray-400" />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-900">
            {listing.title}
          </div>
          <div className="text-xs text-gray-500">
            {listing.building_or_project}
            {listing.unit_identifier ? ` · ${listing.unit_identifier}` : ""}
          </div>
        </div>
      </div>
      <div className="text-sm font-bold text-gray-900">
        {formatPhp(listing.price_gross ?? listing.price)}
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{listing.location}</span>
        <span className="text-gray-300">·</span>
        <span>{formatDateShort(listing.created_at)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2">
        <span className="text-xs font-medium text-gray-500">Broker:</span>
        <span className="text-sm font-semibold text-gray-900">
          {listing.broker.full_name}
        </span>
        {listing.broker.is_verified && (
          <BadgeCheck className="h-3.5 w-3.5 text-[#35622F]" />
        )}
        <span className="text-xs text-gray-400">
          PRC {listing.broker.prc_license_number}
        </span>
        <span className="ml-auto">
          <CoBrokerScorePill score={listing.broker_score} />
        </span>
      </div>
    </div>
  );
}

export default function AdminDisputesPage() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/disputes", {
        params: statusFilter === "All" ? {} : { status: statusFilter },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load disputes"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = useMemo(
    () => items.filter((d) => d.status === "Pending").length,
    [items]
  );

  const resolve = async (dispute, action) => {
    setResolvingId(dispute.id);
    try {
      await api.post(`/admin/disputes/${dispute.id}/resolve`, { action });
      toast.success(
        action === "uphold"
          ? "Exclusive claim upheld. Both brokers notified."
          : "Challenger allowed. Both brokers notified."
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Resolution failed"));
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <section
      className="mx-auto w-full max-w-7xl px-6 py-12 md:px-10 md:py-16 mb-rise"
      data-testid="admin-disputes-page"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#1B2B4B]">
            <Scale className="h-3.5 w-3.5" />
            Admin dispute resolution
          </div>
          <h1 className="mt-3 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Exclusive ATS disputes
          </h1>
          <p
            className="mt-3 max-w-2xl text-base leading-relaxed text-gray-500"
            data-testid="disputes-count"
          >
            {loading
              ? "Loading…"
              : `${items.length} ${statusFilter === "All" ? "total" : statusFilter.toLowerCase()} · ${pendingCount} pending network-wide`}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          data-testid="disputes-refresh-button"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="mt-8">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="rounded-full bg-gray-100 p-1">
            <TabsTrigger
              value="Pending"
              className="mb-transition rounded-full px-4 data-[state=active]:bg-brand-navy data-[state=active]:text-white data-[state=active]:shadow-sm"
              data-testid="disputes-tab-pending"
            >
              Pending
            </TabsTrigger>
            <TabsTrigger
              value="Resolved"
              className="mb-transition rounded-full px-4 data-[state=active]:bg-brand-navy data-[state=active]:text-white data-[state=active]:shadow-sm"
              data-testid="disputes-tab-resolved"
            >
              Resolved
            </TabsTrigger>
            <TabsTrigger
              value="All"
              className="mb-transition rounded-full px-4 data-[state=active]:bg-brand-navy data-[state=active]:text-white data-[state=active]:shadow-sm"
              data-testid="disputes-tab-all"
            >
              All
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="mt-8 space-y-4" data-testid="disputes-loading-skeleton">
          <SkeletonListRow />
          <SkeletonListRow />
        </div>
      ) : items.length === 0 ? (
        <div
          className="mt-10 rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-mb-card"
          data-testid="disputes-empty-state"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#35622F]/10">
            <CheckCircle2 className="h-6 w-6 text-[#35622F]" />
          </div>
          <h3 className="mt-6 text-xl font-semibold text-gray-900">
            {statusFilter === "Pending" ? "All caught up." : "No disputes here."}
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
            {statusFilter === "Pending"
              ? "No exclusive-ATS disputes are awaiting your review."
              : "Switch tabs to see disputes in other statuses."}
          </p>
        </div>
      ) : (
        <ul className="mt-10 grid grid-cols-1 gap-6" data-testid="disputes-list">
          {items.map((d) => {
            const isPending = d.status === "Pending";
            return (
              <li
                key={d.id}
                className="rounded-2xl border border-gray-200 bg-white p-6 shadow-mb-card"
                data-testid={`dispute-row-${d.id}`}
              >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                    <ShieldAlert className="h-3 w-3" />
                    Exclusive ATS collision
                  </span>
                  {d.status === "Pending" ? (
                    <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
                      Pending review
                    </span>
                  ) : (
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                        d.resolution === "upheld"
                          ? "border-[#1B2B4B]/25 bg-[#1B2B4B]/10 text-[#1B2B4B]"
                          : "border-[#35622F]/25 bg-[#35622F]/10 text-[#35622F]"
                      }`}
                      data-testid={`dispute-resolution-${d.id}`}
                    >
                      Resolved · {d.resolution === "upheld" ? "Upheld" : "Allowed"}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-gray-400">
                    Filed {formatDateShort(d.created_at)}
                    {d.resolved_at ? ` · Resolved ${formatDateShort(d.resolved_at)}` : ""}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <DisputeSide
                    label="Exclusive holder"
                    listing={d.exclusive_holder}
                    holderChip={
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#35622F]/25 bg-[#35622F]/10 px-2 py-0.5 text-[11px] font-semibold text-[#35622F]">
                        <BadgeCheck className="h-3 w-3" />
                        Exclusive ATS
                      </span>
                    }
                  />
                  <DisputeSide
                    label="Challenger"
                    listing={d.challenger}
                    holderChip={
                      <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                        Filed new listing
                      </span>
                    }
                  />
                </div>

                {isPending && (
                  <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => resolve(d, "allow")}
                      disabled={resolvingId === d.id}
                      className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      data-testid={`resolve-allow-button-${d.id}`}
                    >
                      {resolvingId === d.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <XCircle className="mr-2 h-4 w-4" />
                      )}
                      Allow challenger
                    </Button>
                    <Button
                      type="button"
                      onClick={() => resolve(d, "uphold")}
                      disabled={resolvingId === d.id}
                      className="mb-focus-ring h-10 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
                      data-testid={`resolve-uphold-button-${d.id}`}
                    >
                      {resolvingId === d.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Uphold exclusive
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
